import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Shared sharing utility for all components
export const shareWithAttachment = (blob: Blob, fileName: string, format: string, recordCount: number) => {
  try {
    // First download the file
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Try Web Share API with file attachment
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], fileName, { type: blob.type })] })) {
      navigator.share({
        title: `${format} Export - ${recordCount} records`,
        text: `Please find the ${format.toLowerCase()} file attached.`,
        files: [new File([blob], fileName, { type: blob.type })]
      }).catch((error) => {
        console.log('Web Share API failed, trying email fallback:', error);
        openEmailClient(format, recordCount);
      });
    } else {
      // Fallback to email client
      openEmailClient(format, recordCount);
    }
  } catch (error) {
    console.error('Error sharing file:', error);
    openEmailClient(format, recordCount);
  }
};

// Helper function to open email client
const openEmailClient = (format: string, recordCount: number) => {
  const subject = `${format} Export - ${recordCount} records`;
  const body = `Please find the ${format.toLowerCase()} file attached.\n\nTotal records: ${recordCount}\n\nThis ${format} file contains all data in a formatted document.`;
  
  // Try Gmail first
  const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const newWindow = window.open(gmailLink, '_blank');
  
  if (!newWindow) {
    // Fallback to mailto
    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    const link = document.createElement('a');
    link.href = mailtoLink;
    link.target = '_blank';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// CSV sharing function
export const shareCSV = (data: any[], headers: string[], fileName: string) => {
  if (data.length === 0) {
    alert('No data to share');
    return;
  }

  try {
    const csv = [headers, ...data].map(row => 
      row.map((field: any) => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    shareWithAttachment(blob, fileName, 'CSV', data.length);
  } catch (error) {
    console.error('Error generating CSV file:', error);
    alert('Error generating CSV file. Please try again.');
  }
};

// PDF sharing function
export const sharePDF = (data: any[], headers: string[], fileName: string, title: string) => {
  if (data.length === 0) {
    alert('No data to share');
    return;
  }

  try {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text(title, 14, 22);
    
    // Add subtitle with count
    doc.setFontSize(12);
    doc.text(`Total Records: ${data.length}`, 14, 30);
    
    // Prepare table data
    const tableData = data.map((item, index) => [
      index + 1,
      ...headers.map(header => {
        const value = item[header.toLowerCase().replace(/\s+/g, '_')] || item[header] || '';
        return String(value).replace(/\n/g, ' ');
      })
    ]);

    // Add table
    autoTable(doc, {
      head: [['#', ...headers]],
      body: tableData,
      startY: 40,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
    });

    // Get PDF as blob for sharing
    const pdfBlob = doc.output('blob');
    shareWithAttachment(pdfBlob, fileName, 'PDF', data.length);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error generating PDF. Please try again.');
  }
};

// Excel sharing function
export const shareExcel = (data: any[], headers: string[], fileName: string) => {
  if (data.length === 0) {
    alert('No data to share');
    return;
  }

  try {
    // Prepare data for Excel
    const excelData = data.map((item, index) => ({
      '#': index + 1,
      ...headers.reduce((acc, header) => {
        const value = item[header.toLowerCase().replace(/\s+/g, '_')] || item[header] || '';
        acc[header] = String(value).replace(/\n/g, ' ');
        return acc;
      }, {} as any)
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    ws['!cols'] = headers.map(() => ({ wch: 20 }));
    
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    
    // Generate Excel file as blob
    const excelBlob = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBlob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    shareWithAttachment(blob, fileName, 'Excel', data.length);
  } catch (error) {
    console.error('Error generating Excel file:', error);
    alert('Error generating Excel file. Please try again.');
  }
};

// Word sharing function
export const shareWord = (data: any[], headers: string[], fileName: string, title: string) => {
  if (data.length === 0) {
    alert('No data to share');
    return;
  }

  try {
    // Create table rows
    const tableRows = data.map((item, index) => `
      <tr>
        <td>${index + 1}</td>
        ${headers.map(header => {
          const value = item[header.toLowerCase().replace(/\s+/g, '_')] || item[header] || '';
          return `<td>${String(value).replace(/\n/g, ' ')}</td>`;
        }).join('')}
      </tr>
    `).join('');

    const wordContent = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<meta name="ProgId" content="Word.Document">
<meta name="Generator" content="Microsoft Word 15">
<meta name="Originator" content="Microsoft Word 15">
<!--[if gte mso 9]><xml>
<w:WordDocument>
<w:View>Print</w:View>
<w:Zoom>90</w:Zoom>
<w:DoNotOptimizeForBrowser/>
</w:WordDocument>
</xml><![endif]-->
<style>
table { border-collapse: collapse; width: 100%; margin: 20px 0; }
th, td { border: 1px solid #000; padding: 8px; text-align: left; }
th { background-color: #428BCA; color: white; font-weight: bold; }
tr:nth-child(even) { background-color: #f2f2f2; }
h1 { color: #333; text-align: center; }
p { font-size: 14px; margin: 10px 0; }
</style>
</head>
<body>
<h1>${title}</h1>
<p><strong>Total Records:</strong> ${data.length}</p>
<table>
  <thead>
    <tr>
      <th>#</th>
      ${headers.map(header => `<th>${header}</th>`).join('')}
    </tr>
  </thead>
  <tbody>
    ${tableRows}
  </tbody>
</table>
</body>
</html>`;
    
    // Create Word document blob
    const blob = new Blob([wordContent], { type: 'application/msword' });
    shareWithAttachment(blob, fileName, 'Word', data.length);
  } catch (error) {
    console.error('Error generating Word document:', error);
    alert('Error generating Word document. Please try again.');
  }
};
